import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "../../assets/Banner.png";
import { FaLinkedinIn, FaTwitter, FaInstagram } from "react-icons/fa";
import { BsFacebook } from "react-icons/bs";
import { ExplodedPieChart } from "../analytics/BudgetChart";
import QuoteChart from "../analytics/QuoteChart";
import Carousel from "../Carousel";
import { showError, showLoading, showSuccess } from "../Ui/ToastNotification";
import toast from "react-hot-toast";
import { formatDate } from "../../utils/Date";
import getImage from "../../assets/Assets";
import api from "../../services/Api";
import { BeatLoader } from "react-spinners";

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const UserImage = getImage("userImage");
  const Envelope = getImage("envelope");
  const Phone = getImage("phone");
  const Maps = getImage("map");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetData, setBudgetData] = useState([]);
  const [quoteData, setQuoteData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`/admin/user/user/${id}`);

        if (response.data.user === null) {
          setUser(null);
          setLoading(false);
          return;
        }

        const userData = {
          ...response.data.user,
          imageUrl: response?.data?.user?.profilePictureUrl || UserImage,
          business: response?.data?.user?.business
            ? Array.from(
                new Set(response.data.user.business.map((b) => b._id))
              ).map((id) =>
                response.data.user.business.find((b) => b._id === id)
              )
            : [],
          quotes: response?.data?.user?.quotes || [],
        };

        const processedBudgetData = [["Business Name", "Percentage"]];
        if (userData.business.length > 0) {
          userData.business.forEach((biz) => {
            processedBudgetData.push([
              biz.name,
              100 / userData.business.length,
            ]);
          });
        }
        setBudgetData(processedBudgetData);

        const processedQuotes = processQuotesData(userData.quotes);
        setQuoteData(processedQuotes);

        setUser(userData);
        toast.dismiss(showLoading);
        showSuccess("User data loaded successfully!");
      } catch (error) {
        console.error(error);
        setUser(null);
        toast.dismiss(showLoading);
        showError("Failed to load user data!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const processQuotesData = (quotes) => {
    if (!quotes || quotes.length === 0) {
      return [];
    }

    const dayCount = {};
    quotes.forEach((quote) => {
      if (quote?.createdAt) {
        const day = new Date(quote.createdAt).toISOString().split("T")[0];
        dayCount[day] = (dayCount[day] || 0) + 1;
      }
    });

    return Object.entries(dayCount).map(([day, count]) => [day, count]);
  };
  const applications = user?.applications || [];

  const socialIcons = {
    instagram: <FaInstagram />,
    facebook: <BsFacebook />,
    twitter: <FaTwitter />,
    linkedin: <FaLinkedinIn />,
  };

  if (loading) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center">
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  if (user === null) {
    return (
      <div className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6]">
        <div
          className="flex items-center justify-center h-screen"
          data-testid="user-not-found"
        >
          User not found
        </div>
      </div>
    );
  }

  const handleCompanyClick = (companyId) => {
    navigate(`/business/list/${companyId}`);
    showSuccess("Navigating to company details...");
  };
  return (
    <div className="ml-64 flex-grow w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6]">
      <div className="container">
        <div className="p-3">
          <div className="relative bg-white w-full h-44 rounded-lg mx-auto mt-2">
            <div className="relative w-full overflow-hidden rounded-t-lg h-2/3">
              <img
                className="object-cover w-full h-full"
                src={Banner}
                alt="alt"
              />
            </div>
            <div className="absolute bottom-0 flex items-end left-8">
              <div className="pb-6">
                <img
                  className="w-32 h-32 border-4 border-white border-solid rounded-full"
                  src={user?.imageUrl}
                  alt="user"
                />
              </div>
              <div className="flex flex-col justify-end mb-6 ml-4">
                <p className="font-nohemi font-semibold text-base text-[#191B1C]">
                  {user?.firstName || "-"} {user?.lastName}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-row w-full mx-auto mt-4">
            <div className="mr-2 w-80">
              <div className="w-full bg-white rounded-lg mb-2 px-4 min-h-0">
                <div className="items-start justify-center w-full py-1 border-b">
                  <p className="font-nohemi text-sm font-semibold text-[#191B1C] py-2">
                    Contact Info
                  </p>
                </div>
                <div className="w-full py-3">
                  <div className="flex mt-4 mb-2">
                    <div className="flex items-start justify-center">
                      <img className="w-4 h-4" src={Envelope} alt="envelope" />
                    </div>

                    <div className="flex-1 ml-2 min-w-0">
                      <p className="font-nohemi font-light text-sm text-[#959FA3]">
                        Email
                      </p>
                      <p className="font-nohemi font-light text-sm text-[#191B1C] mb-3 break-words">
                        {user?.email || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex my-2">
                    <div className="flex items-start justify-center">
                      <img className="w-4 h-4" src={Phone} alt="phone" />
                    </div>

                    <div className="flex-1 ml-2 min-w-0">
                      <p className="font-nohemi font-light text-sm text-[#959FA3]">
                        Phone
                      </p>
                      <p className="font-nohemi font-light text-sm text-[#191B1C] mb-3 break-words">
                        {user?.phoneNumber || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex my-2">
                    <div className="flex items-start justify-center">
                      <img className="w-4 h-4" src={Maps} alt="location" />
                    </div>

                    <div className="flex-1 ml-2 min-w-0">
                      <p
                        className="font-nohemi font-light text-sm text-[#959FA3]"
                        data-testid="location-label"
                      >
                        Location
                      </p>
                      <p className="font-nohemi font-light leading-5 text-sm text-[#191B1C] break-words">
                        {user?.homeAddress || "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full bg-white rounded-lg mt-4 h-auto min-h-0 flex flex-col">
                <div className="items-start justify-center w-full border-b">
                  <p className="font-nohemi text-base font-semibold text-[#191B1C] ml-4 py-2">
                    Biography
                  </p>
                </div>
                <div className="flex-1 w-full min-h-0">
                  <div className="mx-4 mt-4">
                    <div>
                      <p className="font-nohemi text-sm font-semibold text-[#191B1C]">
                        About User
                      </p>
                      <p className="font-nohemi font-light text-sm mt-3 text-[#7B878C] break-words">
                        {user?.bio || "-"}
                      </p>
                    </div>
                  </div>
                  <div className="mx-4 mt-3">
                    <table className="font-nohemi font-light text-sm w-full">
                      <tbody>
                        <tr key="full-name">
                          <td className="text-[#959FA3] align-top py-1">
                            Full Name:
                          </td>
                          <td className="text-[#191B1C] align-top py-1 break-words">
                            {user?.firstName || "-"} {user?.lastName}
                          </td>
                        </tr>
                        <tr key="company">
                          <td className="text-[#959FA3] align-top py-1">
                            Phone Number:
                          </td>
                          <td className="text-[#191B1C] align-top py-1 break-words">
                            {user?.phoneNumber || "-"}
                          </td>
                        </tr>
                        <tr key="dob">
                          <td className="text-[#959FA3] align-top py-1">
                            Date of Birth:
                          </td>
                          <td className="text-[#191B1C] align-top py-1 break-words">
                            {formatDate(user?.dateOfBirth) || "-"}
                          </td>
                        </tr>
                        <tr key="language">
                          <td className="text-[#959FA3] align-top py-1">
                            Language:
                          </td>
                          <td className="text-[#191B1C] align-top py-1 break-words">
                            {user?.language || "English"}
                          </td>
                        </tr>
                        <tr key="country" data-testid="country-row">
                          <td className="text-[#959FA3] align-top py-1">
                            Country
                          </td>
                          <td className="text-[#191B1C] align-top py-1 break-words">
                            {user?.country || "-"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mx-4 my-3">
                  <p className="font-nohemi text-sm font-light text-[#191B1C]">
                    Connect with me
                  </p>
                  <div className="flex flex-row mt-1 flex-wrap">
                    {user?.socialLink?.map((link) => (
                      <a
                        key={link?.id}
                        href={`https://${link?.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="m-1 p-2 rounded-full bg-[#F0F6FF] hover:bg-[#E0E6FF] transition-colors"
                      >
                        {socialIcons[link?.name]}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right side content*/}
            <div className="w-full mx-1">
              <div className="w-full bg-[#F4F5F6] rounded-lg my-2">
                <div className="grid grid-cols-3 gap-4 px-4">
                  <p className="font-nohemi font-semibold text-base text-[#343C6A]">
                    Active Businesses
                  </p>
                  <p className="col-span-2 font-nohemi font-semibold text-base text-[#343C6A]">
                    Total Quote
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4 pt-1.5">
                  <div className="bg-[#fffcfb] rounded-2xl h-[250px]">
                    <ExplodedPieChart data={budgetData} />
                  </div>
                  <div className="col-span-2 bg-[#fffcfb] rounded-2xl h-[250px]">
                    <QuoteChart
                      lineColor="#FCAA0B"
                      data={
                        quoteData?.length > 0
                          ? [["Day", "Quotes"], ...quoteData]
                          : null
                      }
                    />
                  </div>
                </div>
              </div>
              <Carousel
                data={applications}
                title=""
                onCompanyClick={handleCompanyClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
